// src/pages/HomePage.jsx
import { useState, useEffect } from 'react';
import { CALENDLY_URL } from '../constants';
import Footer from '../components/Footer';
import investigationSoc from './investigation-soc.jpg';
import './HomePage.css';

const HEADLINES = [
  'On-chain fraud detection for non-EVM blockchains',
  'Legal compliance, on autopilot',
  'Stop laundering before it settles',
  '10+ non-EVM chains. One risk engine.',
];

const ROTATE_INTERVAL = 5000;

const SUPPORTED_CHAINS = [
  'Solana', 'Aptos', 'Cosmos', 'Sui', 'Near',
  'Stellar', 'Tron', 'Polkadot', 'Algorand', 'Tezos',
];

const FAQS = [
  {
    q: 'Which blockchains does BLCP support?',
    a: 'BLCP is built specifically for non-EVM ecosystems. We currently cover Solana, Aptos, Cosmos, Sui, Near, Stellar, Tron, Polkadot, Algorand, and Tezos, with new chains added on a rolling basis. Each chain integration includes native parsing of token transfers, bridge flows, staking actions, and contract calls — not just generic transaction hashes — so analysts work with normalized, comparable data across networks.',
  },
  {
    q: 'How does real-time fraud detection work?',
    a: 'A streaming indexer ingests every block as it confirms, normalizes the transaction, and runs it through a layered detection stack: address clustering, behavioral fingerprinting, sanctions screening, and a graph-based risk score. Anything that crosses a configured threshold is surfaced in the dashboard and pushed to your webhook within seconds, so investigations start while the funds are still in motion.',
  },
  {
    q: 'What kinds of laundering schemes does the platform detect?',
    a: 'The detection library covers the full set of patterns commonly seen on non-EVM chains: bridge hopping, mixer use, peel chains, structuring across many small wallets, layered token swaps, NFT wash trading, exchange-deposit obfuscation, and impersonation/scam flows. New schemes are added as they appear in the wild — the underlying graph and behavioral models generalize, so emerging variants are usually caught before a hand-coded rule is written for them.',
  },
  {
    q: 'Are reports regulator-ready?',
    a: 'Yes. Every alert can be exported as a finalized report containing the full transaction graph, address attribution, sanctions hits, narrative summary, and the underlying evidence trail. Reports are deterministic and reproducible — given the same input data, the same report is generated — which makes them suitable for filings, subpoenas, and audit response.',
  },
  {
    q: 'How do customers access the platform — API, dashboard, or on-prem?',
    a: 'All three. The dashboard is the day-to-day workspace for analysts and compliance officers; the REST and streaming APIs cover programmatic ingestion into your own SIEM, case-management, or risk system; and an on-prem deployment is available for customers with strict data-residency requirements (financial institutions, government, regulated VASPs). All three surfaces share the same detection engine and data model.',
  },
  {
    q: 'What is the typical detection latency?',
    a: 'Median end-to-end latency from block confirmation to alert fan-out is under two seconds across all supported chains. For chains with sub-second finality (Solana, Aptos, Sui), the bottleneck is our enrichment pipeline, not the chain itself. Latency targets are measured continuously and surfaced in the platform — if a chain falls behind, you see it before your investigators do.',
  },
  {
    q: 'How are false positives handled?',
    a: 'Every alert carries a precision score and a list of contributing signals, so reviewers can triage in seconds rather than minutes. Confirmed false positives feed back into the detection model — addresses, patterns, and counterparties marked benign by your team are weighted accordingly, so the same flow does not re-alert. Tenant-specific tuning means your false-positive rate drops sharply over the first weeks of operation.',
  },
  {
    q: 'Who is BLCP designed for?',
    a: 'Centralized exchanges, custodians, and brokers exposed to non-EVM deposit flows; regulated VASPs needing AML/CFT coverage outside the Ethereum ecosystem; on-chain investigators in law enforcement and government agencies; and crypto-native financial institutions that need a single risk view across the chains they support. The common thread is operating where Ethereum-only tools leave coverage gaps.',
  },
];

export default function HomePage() {
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setHeadlineIndex((prev) => (prev + 1) % HEADLINES.length);
        setFading(false);
      }, 400);
    }, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="home-page">
      <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="home-nav-cta">
        Book a Demo
      </a>
      <div className="home-hero">
        <div className="home-hero-inner">
          <h1 className={`home-headline ${fading ? 'fading' : ''}`}>
            {HEADLINES[headlineIndex]}
          </h1>

          <div className="home-divider" />

          <div className="home-bottom-row">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="home-cta">
              Book a Demo
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </a>

            <p className="home-tagline">
              Fraud detection and legal compliance for non-EVM blockchains
            </p>

            <div className="home-pills-group">
              <div className="home-pills">
                <div className="home-pill">
                  <span className="home-pill-label">Chains supported</span>
                  <span className="home-pill-value">10+</span>
                </div>
                <div className="home-pill">
                  <span className="home-pill-label">Detection latency</span>
                  <span className="home-pill-value">&lt;2s</span>
                </div>
                <div className="home-pill">
                  <span className="home-pill-label">Transactions scanned</span>
                  <span className="home-pill-value">1B+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="home-features">
        <div className="home-features-inner">
          <div className="home-features-grid">

            <div className="home-card home-card-img-top">
              <div className="home-card-img-area">
                <img src="/card-leverage.png" alt="" />
              </div>
              <h3 className="home-card-title">Real-time fraud detection</h3>
              <p className="home-card-text">
                Flag suspicious flows in seconds. A streaming indexer scores every transaction
                against the full pattern library the moment it confirms.
              </p>
            </div>

            <div className="home-card">
              <h3 className="home-card-title">Cross-chain coverage</h3>
              <p className="home-card-text">
                Trace funds across 10+ non-EVM blockchains with one unified graph of clusters,
                addresses, and bridge flows.
              </p>
              <div className="home-markets-strip">
                {SUPPORTED_CHAINS.map((name) => (
                  <span key={name} className="home-market-pill">
                    <span className="home-market-title">{name}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="home-card home-card-img-top">
              <div className="home-card-img-area home-card-img-area-green">
                <img src="/card-yield.png" alt="" />
              </div>
              <h3 className="home-card-title">Stay ahead of laundering</h3>
              <p className="home-card-text">
                Adaptive models evolve as schemes mutate. New mixers, bridges, and obfuscation
                techniques are catalogued as they emerge in the wild.
              </p>
            </div>

            <div className="home-card home-card-wide home-card-row">
              <div className="home-card-row-text">
                <h3 className="home-card-title">Compliance-grade & audit-ready</h3>
                <p className="home-card-text">
                  Deterministic reports, an immutable audit trail, and infrastructure built to
                  meet regulator and enterprise requirements out of the box.
                </p>
                <div className="home-card-checks">
                  <span className="home-card-check">Regulator-ready reports</span>
                  <span className="home-card-check">Immutable audit trail</span>
                  <span className="home-card-check">GDPR compliant</span>
                  <span className="home-card-check">SOC 2 certified</span>
                </div>
              </div>
              <img src="/card-noncustodial.png" alt="" className="home-card-row-img" />
            </div>

            <div className="home-card">
              <h3 className="home-card-title">Cross-chain risk engine</h3>
              <p className="home-card-text">
                Graph analysis, address clustering, and sanctions screening fused into a single
                risk score per transaction — explainable, auditable, and tunable per tenant.
              </p>
            </div>

          </div>
        </div>
      </div>
      <div className="home-section-divider" />

      <div className="home-process">
        <div className="home-process-inner">
          <div className="home-investigation">
            <img src={investigationSoc} alt="" className="home-investigation-img" />
            <div className="home-investigation-text">
              <h2 className="home-investigation-title">Inside an investigation</h2>
              <p className="home-investigation-body">
                When a transaction crosses the risk threshold, an alert lands in
                the analyst's queue with the full transaction graph already
                assembled. From there it's a guided walk: cluster the
                counterparties, screen them against sanctions lists, attribute
                the source of funds, and export the regulator-ready report.
                What used to take a senior investigator days of manual
                chain-hopping now takes minutes — and every step is logged for
                audit.
              </p>
            </div>
          </div>

          <div className="home-process-steps">
            <div className="home-process-step">
              <div className="home-process-step-num">1</div>
              <h3 className="home-process-step-title">Detect</h3>
              <p className="home-process-step-text">
                Streaming indexer scores every transaction against the pattern
                library the moment it confirms. Suspicious flows surface in
                seconds with a precision score and contributing signals.
              </p>
            </div>
            <div className="home-process-step">
              <div className="home-process-step-num">2</div>
              <h3 className="home-process-step-title">Investigate</h3>
              <p className="home-process-step-text">
                Graph analysis, address clustering, and sanctions screening
                fuse into a single view. Attribution and counterparty context
                are pre-loaded so reviewers triage in seconds, not minutes.
              </p>
            </div>
            <div className="home-process-step">
              <div className="home-process-step-num">3</div>
              <h3 className="home-process-step-title">Report</h3>
              <p className="home-process-step-text">
                Deterministic, regulator-ready filings export in one click —
                full transaction graph, address attribution, sanctions hits,
                and an immutable evidence trail through to filing.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="home-faq">
        <div className="home-faq-inner">
          <h2 className="home-faq-title">FAQ</h2>
          <div className="home-faq-list">
            {FAQS.map((item, i) => (
              <div key={i} className="home-faq-item">
                <button
                  className={`home-faq-question ${openFaq === i ? 'open' : ''}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <span>{item.q}</span>
                  <span className="home-faq-icon">{openFaq === i ? '×' : '+'}</span>
                </button>
                {openFaq === i && (
                  <p className="home-faq-answer">{item.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
